import React from 'react';

/**
 * Global React ErrorBoundary — catches render-time JS errors and shows a friendly
 * fallback instead of a white screen.  Logs error details to console for debugging.
 * Used to wrap the top-level <Routes> in App.js.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught:', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary-fallback"
          className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6"
          style={{ fontFamily: 'Inter, "IBM Plex Sans", sans-serif' }}
        >
          <div className="max-w-md w-full text-center">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-rose-600 mb-4">
              Something Broke
            </div>
            <h1
              className="text-4xl sm:text-5xl font-black tracking-tight text-black leading-[1] mb-4"
              style={{ fontFamily: '"IBM Plex Sans", Inter, sans-serif' }}
            >
              We hit a snag.
            </h1>
            <p className="text-base text-black/60 leading-relaxed mb-8">
              An unexpected error occurred while rendering this page. The Founderboard team has been notified. Please reload, or head back home.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                data-testid="error-boundary-reload-btn"
                onClick={this.handleReload}
                className="px-6 py-3 bg-black text-white text-xs uppercase tracking-[0.15em] font-bold hover:bg-black/85 transition-colors"
              >
                Reload page
              </button>
              <button
                data-testid="error-boundary-home-btn"
                onClick={this.handleHome}
                className="px-6 py-3 border border-black text-black text-xs uppercase tracking-[0.15em] font-bold hover:bg-black hover:text-white transition-colors"
              >
                Back to home
              </button>
            </div>
            {this.state.error?.message && (
              <details className="mt-8 text-left text-xs text-black/40 font-mono">
                <summary className="cursor-pointer hover:text-black/60">Show technical details</summary>
                <pre className="mt-2 p-3 bg-black/5 overflow-auto whitespace-pre-wrap break-words">
                  {String(this.state.error.message)}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
